import { apiClient } from '../config/api'

export type UserRole = 'ADMIN' | 'OPERATOR' | 'VIEWER'
export type MfaMethod = 'totp' | 'email' | 'recovery'
export type AccountType = 'CUSTOMER' | 'FLEET_TEAM'
export type DataScope = 'GLOBAL' | 'SELECTED_CUSTOMERS'

export interface RoleTemplate {
  id: number
  name: string
  displayName: string
  description?: string | null
  family: AccountType
  isSystem: boolean
  isActive: boolean
}

export interface LoginCredentials {
  username: string
  password: string
}

export interface Workspace {
  membershipId?: number | null
  customerId: number
  customerName: string
  role?: UserRole | null
  roleTemplateId?: number | null
  roleTemplate?: RoleTemplate | null
  status: string
  isPrimary?: boolean
  platformEnabled: boolean
}

export interface MfaSetup {
  secret?: string
  otpauth_uri?: string
  qr_data_url?: string
}

export interface MfaChallenge {
  challengeToken: string
  methods: MfaMethod[]
  enrollmentRequired: boolean
  setup?: MfaSetup
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type?: string
  expires_in?: number
  user_id?: number
  username?: string
  role?: UserRole
  full_name?: string
  customer_id?: number | null
  platforms?: string[]
  capabilities?: string[]
  workspaces?: Workspace[]
  isPlatformAdmin?: boolean
  accountType?: AccountType
  dataScope?: DataScope
  roleTemplateId?: number | null
  roleTemplate?: RoleTemplate | null
  recovery_codes?: string[]
}

export interface User {
  id: number
  username: string
  fullName: string
  email?: string
  role: UserRole
  customerId?: number | null
  isActive: boolean
  createdAt?: string
  lastLoginAt?: string
  capabilities: string[]
  platforms: string[]
  workspaces: Workspace[]
  activeCustomerId: number | null
  isPlatformAdmin: boolean
  accountType: AccountType
  dataScope: DataScope
  roleTemplateId?: number | null
  roleTemplate?: RoleTemplate | null
}

export type AuthResult =
  | { status: 'authenticated'; user: User; recoveryCodes?: string[] }
  | { status: 'mfa_required'; challenge: MfaChallenge }
  | { status: 'error'; error: string }

const ACCESS_TOKEN_KEY = 'fleet_access_token'
const REFRESH_TOKEN_KEY = 'fleet_refresh_token'
const USER_DATA_KEY = 'fleet_user_data'

const normalizeRoleTemplate = (row: any): RoleTemplate | null => row ? ({
  id: Number(row.id),
  name: String(row.name || ''),
  displayName: String(row.displayName ?? row.display_name ?? row.name ?? ''),
  description: row.description ?? null,
  family: String(row.family || 'CUSTOMER').toUpperCase() as AccountType,
  isSystem: Boolean(row.isSystem ?? row.is_system),
  isActive: Boolean(row.isActive ?? row.is_active ?? true)
}) : null

const normalizeWorkspace = (row: any): Workspace => ({
  membershipId: row.membershipId == null && row.membership_id == null
    ? null
    : Number(row.membershipId ?? row.membership_id),
  customerId: Number(row.customerId ?? row.customer_id),
  customerName: String(row.customerName ?? row.customer_name ?? ''),
  role: row.role ? String(row.role).toUpperCase() as UserRole : null,
  roleTemplateId: row.roleTemplateId == null && row.role_template_id == null
    ? null
    : Number(row.roleTemplateId ?? row.role_template_id),
  roleTemplate: normalizeRoleTemplate(row.roleTemplate ?? row.role_template),
  status: String(row.status || 'ACTIVE'),
  isPrimary: Boolean(row.isPrimary ?? row.is_primary),
  platformEnabled: row.platformEnabled ?? row.platform_enabled ?? true
})

const normalizeUser = (row: any, fallback?: User | null): User => ({
  id: Number(row.id ?? row.user_id ?? fallback?.id ?? 0),
  username: String(row.username ?? fallback?.username ?? ''),
  fullName: String(row.fullName ?? row.full_name ?? fallback?.fullName ?? ''),
  email: row.email ?? fallback?.email,
  role: (row.role ?? fallback?.role ?? 'VIEWER') as UserRole,
  customerId: row.customerId ?? row.customer_id ?? fallback?.customerId ?? null,
  isActive: row.isActive ?? row.is_active ?? fallback?.isActive ?? true,
  createdAt: row.createdAt ?? row.created_at ?? fallback?.createdAt,
  lastLoginAt: row.lastLoginAt ?? row.last_login_at ?? fallback?.lastLoginAt,
  capabilities: Array.isArray(row.capabilities) ? row.capabilities.map(String) : (fallback?.capabilities ?? []),
  platforms: Array.isArray(row.platforms) ? row.platforms.map(String) : (fallback?.platforms ?? []),
  workspaces: Array.isArray(row.workspaces)
    ? row.workspaces.map(normalizeWorkspace).filter((workspace: Workspace) =>
      workspace.status === 'ACTIVE' && workspace.platformEnabled)
    : (fallback?.workspaces ?? []),
  activeCustomerId: row.activeCustomerId ?? row.active_customer_id ?? row.customerId ?? row.customer_id ?? fallback?.activeCustomerId ?? null,
  isPlatformAdmin: Boolean(row.isPlatformAdmin ?? row.is_platform_admin ?? fallback?.isPlatformAdmin ?? false),
  accountType: String(row.accountType ?? row.account_type ?? fallback?.accountType ?? 'CUSTOMER').toUpperCase() as AccountType,
  dataScope: String(row.dataScope ?? row.data_scope ?? fallback?.dataScope ?? 'SELECTED_CUSTOMERS').toUpperCase() as DataScope,
  roleTemplateId: row.roleTemplateId == null && row.role_template_id == null
    ? (fallback?.roleTemplateId ?? null)
    : Number(row.roleTemplateId ?? row.role_template_id),
  roleTemplate: normalizeRoleTemplate(row.roleTemplate ?? row.role_template) ?? fallback?.roleTemplate ?? null
})

const getErrorMessage = (error: any, fallback: string): string => {
  if (error?.response?.status === 401) return 'Invalid username, password, or verification code'
  if (error?.response?.status === 403) return 'This account is not permitted to sign in'
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback
}

class AuthService {
  private accessToken: string | null = null
  private refreshToken: string | null = null
  private user: User | null = null

  constructor() {
    this.loadTokensFromStorage()
  }

  private loadTokensFromStorage(): void {
    try {
      this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
      this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      const userData = localStorage.getItem(USER_DATA_KEY)
      this.user = userData ? normalizeUser(JSON.parse(userData)) : null
    } catch {
      this.clearTokens()
    }
  }

  private saveTokensToStorage(response: TokenResponse): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, response.access_token)
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)
    this.accessToken = response.access_token
    this.refreshToken = response.refresh_token
    this.user = normalizeUser(response, this.user)
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(this.user))
  }

  private clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_DATA_KEY)
    this.accessToken = null
    this.refreshToken = null
    this.user = null
  }

  private parseChallenge(data: any): MfaChallenge {
    return {
      challengeToken: String(data.challenge_token ?? data.challengeToken ?? ''),
      methods: Array.isArray(data.methods) ? data.methods.filter((method: string) =>
        ['totp', 'email', 'recovery'].includes(method)) as MfaMethod[] : ['totp'],
      enrollmentRequired: Boolean(data.enrollment_required ?? data.enrollmentRequired),
      setup: data.setup
    }
  }

  private async consumeAuthResponse(data: any): Promise<AuthResult> {
    if (data?.mfa_required || data?.challenge_token || data?.challengeToken) {
      return { status: 'mfa_required', challenge: this.parseChallenge(data) }
    }
    if (!data?.access_token || !data?.refresh_token) {
      return { status: 'error', error: 'Invalid authentication response' }
    }
    this.saveTokensToStorage(data as TokenResponse)
    const user = await this.getCurrentUser() || this.user
    if (!user) return { status: 'error', error: 'Unable to load the signed-in user' }
    return {
      status: 'authenticated',
      user,
      recoveryCodes: Array.isArray(data.recovery_codes) ? data.recovery_codes : undefined
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      const response = await apiClient.post('/auth/login', credentials)
      return this.consumeAuthResponse(response.data)
    } catch (error: any) {
      return { status: 'error', error: getErrorMessage(error, 'Login failed. Please try again.') }
    }
  }

  async sendMfaEmail(challengeToken: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/auth/mfa/email/send', { challengeToken })
      return { success: true }
    } catch (error: any) {
      return { success: false, error: getErrorMessage(error, 'Unable to send an email code') }
    }
  }

  async sendMfaEnrollmentEmail(challengeToken: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/auth/mfa/enroll/email/send', { challengeToken })
      return { success: true }
    } catch (error: any) {
      return { success: false, error: getErrorMessage(error, 'Unable to send an email code') }
    }
  }

  async verifyMfa(challengeToken: string, method: MfaMethod, code: string): Promise<AuthResult> {
    try {
      const response = await apiClient.post('/auth/mfa/verify', { challengeToken, method, code })
      return this.consumeAuthResponse(response.data)
    } catch (error: any) {
      return { status: 'error', error: getErrorMessage(error, 'Verification failed') }
    }
  }

  async confirmMfaEnrollment(challengeToken: string, method: Exclude<MfaMethod, 'recovery'>, code: string): Promise<AuthResult> {
    try {
      const endpoint = method === 'email'
        ? '/auth/mfa/enroll/email/confirm'
        : '/auth/mfa/enroll/confirm'
      const response = await apiClient.post(endpoint, { challengeToken, code })
      return this.consumeAuthResponse(response.data)
    } catch (error: any) {
      return { status: 'error', error: getErrorMessage(error, 'Two-factor setup could not be confirmed') }
    }
  }

  async acceptInvitation(token: string, password: string): Promise<AuthResult> {
    try {
      const response = await apiClient.post('/auth/invitations/accept', { token, password })
      return this.consumeAuthResponse(response.data)
    } catch (error: any) {
      return { status: 'error', error: getErrorMessage(error, 'This invitation is invalid or has expired') }
    }
  }

  async switchWorkspace(customerId: number | null): Promise<AuthResult> {
    try {
      const response = await apiClient.post('/auth/workspace', { customerId })
      return this.consumeAuthResponse(response.data)
    } catch (error: any) {
      return { status: 'error', error: getErrorMessage(error, 'Unable to switch workspace') }
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await apiClient.post('/auth/logout', { refreshToken: this.refreshToken }, {
          headers: { Authorization: `Bearer ${this.accessToken}` }
        })
      }
    } catch {
      // Local logout must still complete when the server is unavailable.
    } finally {
      this.clearTokens()
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false
    try {
      const response = await apiClient.post('/auth/refresh', { refreshToken: this.refreshToken })
      if (!response.data?.access_token) return false
      this.saveTokensToStorage(response.data)
      return true
    } catch {
      this.clearTokens()
      return false
    }
  }

  async getCurrentUser(): Promise<User | null> {
    if (!this.accessToken) return null
    try {
      const response = await apiClient.get('/auth/me', undefined, {
        headers: { Authorization: `Bearer ${this.accessToken}` }
      })
      this.user = normalizeUser(response.data?.user ?? response.data, this.user)
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(this.user))
      return this.user
    } catch (error: any) {
      if (error?.response?.status === 401 || String(error?.message).includes('401')) {
        const refreshed = await this.refreshAccessToken()
        if (refreshed) return this.getCurrentUser()
        this.clearTokens()
      }
      return null
    }
  }

  isAuthenticated(): boolean {
    return Boolean(this.accessToken && this.user)
  }

  getAccessToken(): string | null {
    return this.accessToken
  }

  getCurrentUserData(): User | null {
    return this.user
  }
}

export const authService = new AuthService()
export default authService
